import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Alert, Box} from "@mui/material";
import {
    DatePicker,
    DateRangePicker,
    LocalizationProvider,
    SingleInputDateRangeField,
} from "@mui/x-date-pickers-pro";
import {AdapterLuxon} from "@mui/x-date-pickers-pro/AdapterLuxon";
import {debounce} from "lodash";
import {
    INCLUDE_EXCLUDE_BORDER,
} from "../IncludeExcludeFilter";
import {
    FilterAction,
    FilterOption,
    FilterState,
    IncludeExcludeFilterCategory,
} from "../reducers/includeExcludeFilterReducer";
import {TreeViewCheckboxContent} from "./TreeViewCheckboxContent";
import {TreeViewFilterSelectionHeader} from "./TreeViewFilterSelectionHeader";
import {
    MINIMUM_CHAR_LENGTH_FOR_SERVER_SEARCH,
    type SelectAllState,
    buildTreeItems,
    filterTreeOptionsBySearchTerm,
    getDisplayLabel,
    sortFilterOptions,
} from "./treeViewFilterListSelectionUtils";

type TreeViewFilterListSelectionProps = {
    validCategories: IncludeExcludeFilterCategory[];
    selectedCategory: string;
    filteredOptions: {
        include: IncludeExcludeFilterCategory[];
        exclude: IncludeExcludeFilterCategory[];
    };
    activeTab: number;
    setActiveTab: (tabIndex: number) => void;
    filterSelectionState: FilterState;
    dispatch: React.Dispatch<FilterAction>;
    handleServerSearch?: (categoryId: string, search: string) => void;
    resetSearchSignal?: number;
    serverSearchLoading?: boolean;
    serverSearchMinChars?: number;
    onModeSwitch?: (filterType: "include" | "exclude") => void;
};

function DateSelectionContent({
    options,
    selectedCategory,
    selectedCategoryData,
    getType,
    dispatch,
}: {
    options: FilterOption[];
    selectedCategory: string;
    selectedCategoryData: IncludeExcludeFilterCategory;
    getType: () => "include" | "exclude";
    dispatch: React.Dispatch<FilterAction>;
}) {
    if (selectedCategoryData.type === "date-range") {
        return (
            <LocalizationProvider dateAdapter={AdapterLuxon}>
                <Box sx={{mt: 2, mx: 2, width: "auto"}}>
                    {options.map((option) => (
                        <DateRangePicker
                            sx={{width: "100%"}}
                            slots={{field: SingleInputDateRangeField}}
                            slotProps={{
                                field: {
                                    // @ts-expect-error MUI date range field accepts this at runtime
                                    InputLabelProps: {shrink: true},
                                },
                            }}
                            key={`${selectedCategory}-${option.item}`}
                            label={getDisplayLabel(option) || "Date Range"}
                            value={
                                Array.isArray(option.dateRange) && option.dateRange.length === 2
                                    ? option.dateRange
                                    : undefined
                            }
                            onChange={(newValue) =>
                                dispatch({
                                    type: "SET_DATE_RANGE",
                                    payload: {
                                        filterType: getType(),
                                        parentCategoryId: selectedCategory,
                                        categoryId: option.categoryId,
                                        dateRange: newValue,
                                    },
                                })
                            }
                        />
                    ))}
                </Box>
            </LocalizationProvider>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterLuxon}>
            <Box sx={{display: "flex", flexDirection: "column", gap: 2, mt: 2, mx: 2}}>
                {options.map((option) => (
                    <DatePicker
                        sx={{width: "100%"}}
                        key={`${selectedCategory}-${option.item}`}
                        label={getDisplayLabel(option)}
                        value={option.dateValue ?? null}
                        format="M/d/yyyy"
                        slotProps={{
                            actionBar: {
                                actions: ["clear"],
                            },
                            textField: {
                                fullWidth: true,
                                size: "small",
                                InputLabelProps: {shrink: true},
                            },
                        }}
                        onChange={(newValue) =>
                            dispatch({
                                type: "SET_DATE_OPERATOR",
                                payload: {
                                    filterType: getType(),
                                    categoryId: selectedCategory,
                                    operator:
                                        option.dateOperator ??
                                        (option.item.toLowerCase() as "after" | "before"),
                                    dateValue: newValue,
                                },
                            })
                        }
                    />
                ))}
            </Box>
        </LocalizationProvider>
    );
}

export const TreeViewFilterListSelection = ({
    validCategories,
    selectedCategory,
    filteredOptions,
    activeTab,
    setActiveTab,
    filterSelectionState,
    dispatch,
    handleServerSearch,
    resetSearchSignal,
    serverSearchLoading,
    onModeSwitch,
}: TreeViewFilterListSelectionProps) => {
    // This shell component coordinates the extracted header/tree/date subcomponents
    // and owns the UI-only state that needs to persist across child rerenders.
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [serverError, setServerError] = useState<string | null>(null);

    const debouncedSetSearch = useMemo(
        () => debounce((value: string) => {
            setDebouncedSearchTerm(value);
        }, 250),
        [],
    );

    const debouncedHandleServerSearch = useMemo(
        () => debounce((categoryId: string, value: string) => {
            handleServerSearch?.(categoryId, value);
        }, 250),
        [handleServerSearch],
    );

    useEffect(() => {
        return () => {
            debouncedSetSearch.cancel();
            debouncedHandleServerSearch.cancel();
        };
    }, [debouncedSetSearch, debouncedHandleServerSearch]);

    useEffect(() => {
        debouncedHandleServerSearch.cancel();
    }, [activeTab, selectedCategory, debouncedHandleServerSearch]);

    useEffect(() => {
        if (resetSearchSignal !== undefined) {
            setSearchTerm("");
            setDebouncedSearchTerm("");
            debouncedSetSearch.cancel();
            debouncedHandleServerSearch.cancel();
            setServerError(null);
        }
    }, [resetSearchSignal, debouncedSetSearch, debouncedHandleServerSearch]);

    const getType = () => (activeTab === 0 ? "include" : "exclude");

    const selectedCategoryData = filterSelectionState.options[getType()].find(
        (cat) => cat.id === selectedCategory,
    );

    const optionsList = filteredOptions[activeTab === 0 ? "include" : "exclude"];

    const selectedCategoryObj = validCategories.find(
        (cat) => cat.id === selectedCategory,
    );
    const isIncludeOnly = !!selectedCategoryObj?.includeOnly;
    const isServerSide = !!selectedCategoryObj?.serverSideSearch;

    useEffect(() => {
        if (isIncludeOnly && activeTab !== 0) {
            setActiveTab(0);
        }
    }, [isIncludeOnly, activeTab, setActiveTab]);

    // These memoized values are display-only projections of reducer state used by the
    // tree/date views; the reducer remains the source of truth for actual selections.
    const categoryOptions = useMemo(() => {
        const categoryData = optionsList.find((cat) => cat.id === selectedCategory);
        if (!categoryData) return [];

        return sortFilterOptions(
            filterTreeOptionsBySearchTerm(categoryData.options, debouncedSearchTerm),
        );
    }, [optionsList, selectedCategory, debouncedSearchTerm]);

    const treeItems = useMemo(() => buildTreeItems(categoryOptions), [categoryOptions]);
    const isSearchActive =
        debouncedSearchTerm.length >= MINIMUM_CHAR_LENGTH_FOR_SERVER_SEARCH;

    // Selection lives in the reducer's applied filter map, not in each option's
    // `checked` flag (which can go stale on the opposite include/exclude side).
    // Deriving straight from the map keeps the tree's tri-state accurate.
    const activeFilterMap = filterSelectionState.filters[getType()];
    const isChecked = useCallback(
        (option: FilterOption) => {
            const applied = activeFilterMap[option.categoryId];
            return Array.isArray(applied) && (applied as string[]).includes(option.item);
        },
        [activeFilterMap],
    );

    const onToggle = useCallback(
        (option: FilterOption) => {
            dispatch({
                type: "TOGGLE_OPTION",
                payload: {
                    filterType: activeTab === 0 ? "include" : "exclude",
                    topLevelCategoryId: selectedCategory,
                    categoryId: option.categoryId,
                    optionId: option.item,
                },
            });
        },
        [dispatch, selectedCategory, activeTab],
    );

    const selectAllState = useMemo<SelectAllState | null>(() => {
        if (!selectedCategoryData || selectedCategoryData.type !== "checkbox") {
            return null;
        }

        const checkedOptions = selectedCategoryData.options.filter((option) => option.checked);

        return {
            checked:
                selectedCategoryData.options.length > 0 &&
                checkedOptions.length === selectedCategoryData.options.length,
            indeterminate:
                checkedOptions.length > 0 &&
                checkedOptions.length < selectedCategoryData.options.length,
        };
    }, [selectedCategoryData]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Local search filters the current tree immediately, while searchable categories
        // can also delegate to the external search hook once the threshold is met.
        const value = event.target.value;
        setSearchTerm(value);
        debouncedSetSearch(value);
        setServerError(null);

        if (isServerSide && handleServerSearch) {
            if (value.length >= MINIMUM_CHAR_LENGTH_FOR_SERVER_SEARCH) {
                debouncedHandleServerSearch(selectedCategory, value);
            } else {
                debouncedHandleServerSearch.cancel();
                dispatch({
                    type: "CLEAR_SERVER_OPTIONS",
                    payload: {filterType: getType(), categoryId: selectedCategory},
                });
            }
        }
    };

    const handleClearSearch = () => {
        // Clearing resets both the local filter text and any externally-provided server
        // search results so the next render starts from the category's base options.
        setSearchTerm("");
        setDebouncedSearchTerm("");
        debouncedSetSearch.cancel();
        debouncedHandleServerSearch.cancel();
        setServerError(null);

        if (isServerSide) {
            dispatch({
                type: "CLEAR_SERVER_OPTIONS",
                payload: {filterType: getType(), categoryId: selectedCategory},
            });
        }
    };

    return (
        <Box
            flex={1}
            borderRight={INCLUDE_EXCLUDE_BORDER}
            overflow="hidden"
            display="flex"
            flexDirection="column"
            minHeight={0}
        >
            <TreeViewFilterSelectionHeader
                selectedCategory={selectedCategoryObj}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                onModeSwitch={onModeSwitch}
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
                onClearSearch={handleClearSearch}
            />

            {selectedCategoryData?.type === "checkbox" && (
                <TreeViewCheckboxContent
                    isServerSide={isServerSide}
                    serverError={serverError}
                    searchTerm={searchTerm}
                    selectedCategory={selectedCategory}
                    selectedCategoryLabel={selectedCategoryData.label}
                    filterType={getType()}
                    dispatch={dispatch}
                    selectAllState={selectAllState}
                    treeItems={treeItems}
                    isChecked={isChecked}
                    onToggle={onToggle}
                    searchActive={isSearchActive}
                    serverSearchLoading={serverSearchLoading}
                />
            )}
            {selectedCategoryData && selectedCategoryData.type !== "checkbox" && (
                <Box flex={1} minHeight={0} overflow="auto">
                    <DateSelectionContent
                        options={categoryOptions}
                        selectedCategory={selectedCategory}
                        selectedCategoryData={selectedCategoryData}
                        getType={getType}
                        dispatch={dispatch}
                    />
                </Box>
            )}

            {isServerSide && serverError && (
                <Alert severity="error" sx={{m: 2}}>
                    {serverError}
                </Alert>
            )}
        </Box>
    );
};
